import React, { useEffect, useMemo, useRef, useState } from "react";
import EmojiPicker from "emoji-picker-react";
import "./SocialTextInputs.css";

export const HASHTAG_SUGGESTIONS = [
  "love", "instagood", "photooftheday", "fashion", "beautiful", "happy", "cute", "tbt",
  "like4like", "followme", "picoftheday", "follow", "me", "selfie", "summer", "art",
  "friends", "repost", "nature", "girl", "fun", "style", "smile", "food", "instalike",
  "likeforlikes", "family", "travel", "fitness", "amazing", "life", "beauty",
  "explore", "music", "motivation", "sunset", "photo", "lifestyle",
  "business", "smallbusiness", "entrepreneur", "marketing", "socialmedia", "branding",
  "digitalmarketing", "contentcreator", "creativity", "inspiration", "success", "community",
  "shoplocal", "supportsmallbusiness", "handmade", "newproduct", "onlineshopping", "sale",
  "behindthescenes", "productivity", "wellness", "health", "recipe", "foodie", "delicious",
  "coffee", "restaurant", "fashionstyle", "ootd", "skincare", "makeup", "travelgram",
  "wanderlust", "adventure", "photography", "design", "artist", "craft", "homedecor",
  "education", "learn", "tips", "howto", "inspirationdaily", "mondaymotivation",
  "weekend", "celebration", "giveaway", "customerlove", "thankyou", "new", "trending"
].filter((tag) => /^[a-z0-9]+$/.test(tag));

export const HashtagInput = ({ id, value = "", onChange, placeholder = "#brand #topic", className = "form-control" }) => {
  const [focused, setFocused] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const inputRef = useRef(null);
  const currentToken = String(value).split(/[\s,]+/).pop() || "";
  const query = currentToken.replace(/^#/, "").toLowerCase();
  const suggestions = useMemo(() => {
    if (query) return HASHTAG_SUGGESTIONS.filter((tag) => tag.startsWith(query) && `#${tag}` !== currentToken.toLowerCase()).slice(0, 8);
    return currentToken === "#" ? HASHTAG_SUGGESTIONS.slice(0, 8) : [];
  }, [currentToken, query]);

  useEffect(() => setActiveSuggestion(0), [query]);

  const chooseSuggestion = (tag) => {
    const prefix = String(value).slice(0, String(value).length - currentToken.length);
    onChange(`${prefix}#${tag} `);
    setFocused(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (!suggestions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestion((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestion((index) => (index - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter" && focused) {
      event.preventDefault();
      chooseSuggestion(suggestions[activeSuggestion]);
    } else if (event.key === "Escape") {
      setFocused(false);
    }
  };

  return <div className="social-text-field hashtag-field">
    <input
      ref={inputRef}
      id={id}
      className={className}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onKeyDown={handleKeyDown}
      role="combobox"
      autoComplete="off"
      aria-autocomplete="list"
      aria-haspopup="listbox"
      aria-controls={`${id}-suggestions`}
      aria-expanded={focused && suggestions.length > 0}
    />
    {focused && suggestions.length > 0 && <div className="hashtag-suggestions" id={`${id}-suggestions`} role="listbox" aria-label="Suggested hashtags">
      {suggestions.map((tag, index) => <button
        key={tag}
        type="button"
        role="option"
        aria-selected={index === activeSuggestion}
        className="hashtag-suggestion"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => chooseSuggestion(tag)}
      >
        <span className="hashtag-suggestion-mark">#</span>
        <span>{tag}</span>
      </button>)}
    </div>}
  </div>;
};

export const EmojiPickerInput = ({ id, value = "", onChange, maxLength = 255 }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeWhenOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeWhenOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeWhenOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return <div className="social-text-field emoji-field" ref={rootRef}>
    <div className="emoji-input-row">
      <input id={id} className="form-control" value={value} maxLength={maxLength} onChange={(event) => onChange(event.target.value)} />
      <button type="button" className="emoji-picker-toggle" aria-label="Open emoji picker" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        <span aria-hidden="true">😊</span>
      </button>
    </div>
    {open && <div className="emoji-picker-popover">
      <EmojiPicker
        onEmojiClick={({ emoji }) => {
          if (String(value).length + emoji.length <= maxLength) onChange(`${value}${emoji}`);
        }}
        width="100%"
        height={360}
        previewConfig={{ showPreview: false }}
        lazyLoadEmojis
      />
    </div>}
  </div>;
};
